import { IHttpService, ILocationService, IPromise, IQService } from "angular";
import { Ng1StateDeclaration } from "@uirouter/angularjs";
import equal from "fast-deep-equal";
import { encode as base64encode } from "js-base64";
import Cookies from "js-cookie";
import { storage } from "@gtmhub/core/storage";
import { EnvironmentService } from "@gtmhub/env";
import { ISnippet } from "@gtmhub/insights";
import { Language, getLanguageFromLocalStorage, removeLanguageFromLocalStorage } from "@gtmhub/localization";
import { ICurrentUserRole, ICurrentUserRolesStoreState } from "@gtmhub/roles";
import { INgRedux } from "@gtmhub/state-management";
import { IAccess, PermissionsMap, UserAction } from "@webapp/sessions/models/sessions.model";
import { UserProfileService } from "@webapp/user-profile/services/user-profile.service";
import { getCurrentAccountId, getCurrentUserId } from "./current-user-storage";
import { IUser, IUserSettings } from "./models";

export class CurrentUserService {
  public static $inject = ["$http", "EnvironmentService", "UserProfileService", "$location", "$ngRedux", "$q"];

  constructor(
    private $http: IHttpService,
    private env: EnvironmentService,
    private profileService: UserProfileService,
    private $location: ILocationService,
    private $ngRedux: INgRedux,
    private $q: IQService
  ) {}

  public demandsAreMet(toState: Ng1StateDeclaration): boolean {
    // we assume that states that require account information also need the userId information
    const stateRequiresAccount: boolean = toState && toState.data && toState.data.requiresAccount;
    const currentUserId = getCurrentUserId();
    const userLanguage = getLanguageFromLocalStorage();
    const roles = this.getCurrentUserRolesNames(); // used by analyticsService and timelineService -> may be removed

    return !stateRequiresAccount || (currentUserId && userLanguage && !!roles);
  }

  public verifyEmail(): IPromise<unknown> {
    const { email } = this.profileService.getProfile();
    const url = this.env.getApiEndpoint("/users/send-verification-email/" + email);

    return this.$http.post(url, "");
  }

  public validateEmailVerification(emailVerificationCode: string): IPromise<unknown> {
    const id = getCurrentUserId();
    const url = this.env.getApiEndpoint(`/users/${id}/validate-verify-email`);
    const body = {
      emailVerificationCode,
    };

    return this.$http.post(url, body);
  }

  public clear(): void {
    storage.remove("userSettings");
    storage.remove("userId");
    removeLanguageFromLocalStorage();
  }

  public setUserSettings(user: IUser): void {
    if (user.userSettings) {
      storage.set("userSettings", user.userSettings);
    } else {
      // this will be deleted when we migrate all users to have new userSettings property
      storage.set("userSettings", user.settings);
    }
  }

  public setMarketplaceRedirectCookie(): void {
    // Skip the first part of the subdomain
    // The cookie will be shared between subdomains
    const domain = this.$location.host().match(/[^.]*\.[^.]*$/);
    const lastVisitedUrlEncoded = base64encode(this.$location.absUrl().split("?")[0]);
    // Expires in 24h
    const expireDate = new Date();
    expireDate.setDate(expireDate.getDate() + 1);
    Cookies.set("marketplace_redirect", lastVisitedUrlEncoded, { domain: domain[0], expires: expireDate, sameSite: "None", secure: true });
  }

  public removeMarketplaceRedirectCookie(): void {
    // Skip the first part of the subdomain
    // The cookie will be shared between subdomains
    const domain = this.$location.host().match(/[^.]*\.[^.]*$/);
    if (domain) {
      Cookies.remove("marketplace_redirect", { domain: domain[0] });
    }
  }

  public getUserSetting<T = unknown>(key: keyof IUserSettings): T {
    if (!key) {
      throw new Error("You must provide user setting key.");
    }

    const userSettings = this.getUserSettingsFromLocalStorage();
    return userSettings ? (userSettings[key] as T) : null;
  }

  public setUserSetting(userSetting: IUserSettings): IPromise<unknown> {
    const currentUserSettings = this.getUserSettingsFromLocalStorage();

    const areSettingsUnchanged = Object.keys(userSetting || {}).every((settingKey) => equal(userSetting[settingKey], currentUserSettings?.[settingKey]));
    if (areSettingsUnchanged) {
      return this.$q.resolve();
    }

    const userSettings: IUserSettings = {
      ...currentUserSettings,
      ...userSetting,
    };

    storage.set("userSettings", userSettings);

    if (userSetting.snippets) {
      userSetting.snippets = userSetting.snippets.map((snippet: ISnippet) => {
        const code = base64encode(snippet.code || "");
        return {
          ...snippet,
          code,
        };
      });
    }

    const url = this.env.getApiEndpoint("/users/settings");
    return this.$http.patch<unknown>(url, userSetting);
  }

  public raiseProfileLanguageUpdated(language: Language): void {
    const profile = this.profileService.getProfile();
    profile.language = language;
    this.profileService.setProfile(profile, { notify: true });
  }

  public allowedActionsSync(accessObject: IAccess): UserAction[] {
    if (!accessObject) return [];
    const accessIdsMap = this.getAccesIdMap(accessObject);
    const userId = getCurrentUserId();
    const accountId = getCurrentAccountId();
    const userPrincipalIds = [...this.getCurrentUserRolesIds({ withShadowRoles: true }), userId, accountId];

    return userPrincipalIds.reduce((userAllowedActions, principleId) => {
      const allowedActions: string[] = accessIdsMap[principleId];

      if (!allowedActions) {
        return userAllowedActions;
      }

      const currentPrincipleAllowedActions = allowedActions.filter((allowedAction) => !userAllowedActions.includes(allowedAction));

      return [...userAllowedActions, ...currentPrincipleAllowedActions];
    }, []);
  }

  public userHasRole(roleName: string): boolean {
    return this.getCurrentUserRolesNames().indexOf(roleName) !== -1;
  }

  public getCurrentUserRolesNames(filter?: { withShadowRoles: boolean }): string[] {
    const currentUserRolesFromStore: ICurrentUserRole[] = this.getCurrentUserRoles(filter?.withShadowRoles);

    return currentUserRolesFromStore.map((role) => role.name);
  }

  private getCurrentUserRolesIds(filter?: { withShadowRoles: boolean }): string[] {
    const currentUserRolesFromStore: ICurrentUserRole[] = this.getCurrentUserRoles(filter?.withShadowRoles);

    return currentUserRolesFromStore.map((role) => role.id);
  }

  private getCurrentUserRoles(withShadowRoles?: boolean): ICurrentUserRole[] {
    let roles = this.$ngRedux.getState<ICurrentUserRolesStoreState>().currentUserRoles.nonShadowRoles;

    if (withShadowRoles) {
      roles = roles.concat(this.$ngRedux.getState<ICurrentUserRolesStoreState>().currentUserRoles.shadowRoles);
    }

    return roles;
  }

  /*
   * This method is used to get the teams of the current user.
   * */
  public getCurrentUserShadowRoles(): ICurrentUserRole[] {
    return this.$ngRedux.getState<ICurrentUserRolesStoreState>().currentUserRoles.shadowRoles;
  }

  private getUserSettingsFromLocalStorage(): IUserSettings {
    return storage.get("userSettings");
  }

  private getAccesIdMap(access: IAccess): PermissionsMap {
    if (!access) return {};
    return access.permissions.reduce((permissionsMap, permission) => {
      permissionsMap[permission.principalId] = permission.grant.general;

      return permissionsMap;
    }, {});
  }
}
